import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useParams, Navigate } from 'react-router-dom';
import { schemaService } from './services/schemaService';
import ModelManager from './components/ModelManager';
import './App.css';

const ModelRoute = () => {
  const { modelName } = useParams();
  const schema = schemaService.getSchema(modelName);

  if (!schema) {
    return (
      <div className="error">
        <h2>Model not found</h2>
        <p>The model "{modelName}" does not exist.</p>
        <Link to="/">Return to dashboard</Link>
      </div>
    );
  }

  return <ModelManager schema={schema} />;
};

const Dashboard = ({ models }) => {
  return (
    <div className="dashboard">
      <h1>Dynamic CMS Dashboard</h1>
      <p>Schemas loaded automatically from API</p>
      
      {models.length === 0 ? (
        <div className="empty-state">
          <p>No models available.</p>
          <p>Make sure your backend is running and models are registered.</p>
        </div>
      ) : (
        <div className="model-cards">
          {models.map((model) => (
            <Link
              key={model.name}
              to={`/model/${model.name}`}
              className="model-card"
            >
              <h3>{model.displayName}</h3>
              <p>Manage {model.displayName.toLowerCase()}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);

  useEffect(() => {
    loadSchemas();
  }, []);

  const loadSchemas = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all schemas from the API
      await schemaService.loadSchemas();
      
      // Get the list of models
      const modelList = schemaService.getModelList();
      setModels(modelList);
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading schemas:', err);
      setError(err.message || 'Failed to load schemas from API');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <h2>Loading CMS...</h2>
        <p>Fetching schemas from API</p>
        <code>{process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}</code>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-error">
        <h2>Error Loading CMS</h2>
        <p>{error}</p>
        <div className="error-details">
          <h3>Troubleshooting:</h3>
          <ul>
            <li>Make sure your backend is running at: <code>{process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}</code></li>
            <li>Verify the backend has the meta endpoints: <code>GET /_meta/models</code> and <code>GET /_meta/schema/:model</code></li>
            <li>Check the browser console for more details</li>
          </ul>
        </div>
        <button onClick={loadSchemas} className="btn btn-primary">
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="app">
        <nav className="sidebar">
          <div className="sidebar-header">
            <h2>Dynamic CMS</h2>
            <button 
              onClick={loadSchemas} 
              className="refresh-btn"
              title="Refresh schemas from API"
            >
              ↻
            </button>
          </div>
          <ul className="nav-menu">
            <li>
              <Link to="/" className="nav-link">
                <span className="nav-icon">🏠</span> Dashboard
              </Link>
            </li>
            {models.length > 0 && (
              <li className="nav-section">Models ({models.length})</li>
            )}
            {models.map((model) => (
              <li key={model.name}>
                <Link to={`/model/${model.name}`} className="nav-link">
                  <span className="nav-icon">📋</span> {model.displayName}
                </Link>
              </li>
            ))}
          </ul>
          
          <div className="sidebar-footer">
            <div className="status-indicator status-connected"></div>
            <small>Schemas from API</small>
          </div>
        </nav>

        <main className="main-content">
          <Routes>
            <Route path="/" element={<Dashboard models={models} />} />
            <Route path="/model/:modelName" element={<ModelRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

export default App;
